import { useState, useEffect, useCallback } from 'react'
import type { PYQProgress } from '../types'
import { STORAGE_KEYS, RE_ENGAGEMENT_CONFIG } from '../utils/constants'
import { useAccountType } from './useAccountType'

interface UseReEngagementResult {
  showReEngagementPrompt: boolean
  lastProgress: PYQProgress | null
  dismissPrompt: () => void
  saveProgress: (testCode: string, questionIndex: number) => void
  clearProgress: () => void
}

/**
 * Hook to manage re-engagement prompts for users returning after 2+ days
 * Tracks their last PYQ progress and shows "continue where you left off" prompt
 */
export function useReEngagement(): UseReEngagementResult {
  const { isFreeUser } = useAccountType()
  const [lastProgress, setLastProgress] = useState<PYQProgress | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Load progress from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PYQ_PROGRESS)
      if (stored) {
        const progress: PYQProgress = JSON.parse(stored)
        setLastProgress(progress)

        // Check if enough time has passed to show re-engagement prompt
        const daysSinceLastVisit =
          (Date.now() - progress.lastVisitTimestamp) / RE_ENGAGEMENT_CONFIG.MS_PER_DAY

        if (daysSinceLastVisit >= RE_ENGAGEMENT_CONFIG.DAYS_THRESHOLD && isFreeUser) {
          setShowPrompt(true)
        }
      }
    } catch (error) {
      console.error('Failed to load PYQ progress:', error)
    }
  }, [isFreeUser])

  // Save progress to localStorage
  const saveProgress = useCallback((testCode: string, questionIndex: number) => {
    const progress: PYQProgress = {
      lastTestCode: testCode,
      lastQuestionIndex: questionIndex,
      lastVisitTimestamp: Date.now(),
    }

    try {
      localStorage.setItem(STORAGE_KEYS.PYQ_PROGRESS, JSON.stringify(progress))
      setLastProgress(progress)
    } catch (error) {
      console.error('Failed to save PYQ progress:', error)
    }
  }, [])

  // Clear progress from localStorage
  const clearProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.PYQ_PROGRESS)
      setLastProgress(null)
      setShowPrompt(false)
    } catch (error) {
      console.error('Failed to clear PYQ progress:', error)
    }
  }, [])

  // Dismiss the re-engagement prompt
  const dismissPrompt = useCallback(() => {
    setDismissed(true)
    setShowPrompt(false)
    // Update the timestamp so we don't show again immediately
    if (lastProgress) {
      saveProgress(lastProgress.lastTestCode, lastProgress.lastQuestionIndex)
    }
  }, [lastProgress, saveProgress])

  return {
    showReEngagementPrompt: showPrompt && !dismissed && isFreeUser,
    lastProgress,
    dismissPrompt,
    saveProgress,
    clearProgress,
  }
}
